import { UserGroups } from '@ravago/shared/authentication';
import RevalidationTag from '@ravago/shared/page-data/enums/revalidation-tags/tags';
import { CaseStudyList, ContentfulCaseStudy, ContentfulCaseStudyEntry, ContentfulCollectionName } from '@ravago/shared/page-data/models/contentful';
import { BaseQuery } from '@ravago/shared/page-data/models/utils';

import { ContentfulClientApi, EntryCollection } from 'contentful';

import { cache } from '../cache/cache.utils';
import { ContentfulProductUtils } from '../contentful-product/contentful-product.utils';
import { createClient } from '../contentful/client';
import { MarketSegmentUtils } from '../market-segment/market-segment.utils';
import { QueryBuilder } from '../query-builder/query-builder';

/* c8 ignore start */
export class CaseStudyUtils {
  static getCaseStudies = cache(async (locale: string, query: BaseQuery, userGroups: UserGroups[], overwriteClientApi?: ContentfulClientApi): Promise<CaseStudyList> => {
    const contentfulClientApi = overwriteClientApi ?? createClient([RevalidationTag.caseStudies]);
    const contentType: ContentfulCollectionName = 'caseStudy';

    const { limit, orderBy, caseStudyIds, productIds, industryIds, filterBy, brandIds } = query;

    const [productId] = productIds || [];
    const [brandId] = brandIds || [];

    const userInternal = userGroups.includes(UserGroups.ravagoSalesRepresentative) || userGroups.includes(UserGroups.ravagoGlobalGroupAdmin);

    const queryOptions = new QueryBuilder()
      .contentType(contentType)
      .limit(limit || 4)
      .order(orderBy)
      .include(10)
      .locale(locale)
      .setArrayOptional('sys.id[in]', caseStudyIds)
      .setOptional('fields.products.sys.id', productId);

    if (!userInternal) {
      queryOptions.set('fields.visibility[all]', 'External');
    }

    if (brandId) {
      const products = await ContentfulProductUtils.getProductCollection(
        locale,
        {
          limit: 1000,
          filterBy,
          orderBy,
          brandIds: [brandId],
          select: 'sys.id, fields.slug'
        },
        overwriteClientApi
      );

      queryOptions.setArrayOptional(
        'fields.products.sys.id[in]',
        products.items.map((prod) => prod.id)
      );
    }

    if (industryIds?.length) {
      const marketSegments = await MarketSegmentUtils.getMarketSegmentsByQuery(
        locale,
        {
          limit: 1000,
          filterBy,
          orderBy,
          industryIds
        },
        overwriteClientApi
      );
      const marketSegmentIds: Array<string> = marketSegments?.map((marketSegment) => marketSegment.sys.id) || [];

      queryOptions.setArrayOptional('fields.segments.sys.id[in]', marketSegmentIds);
    }

    const entries = await contentfulClientApi.getEntries<ContentfulCaseStudyEntry>(queryOptions.build());
    const response = this.normalizeEntryCollection(entries).slice(0, limit || 4);

    return { caseStudies: response, total: entries.total };
  });

  static getAll = cache(async (contentfulClientApi: ContentfulClientApi, locale: string) => {
    const contentType: ContentfulCollectionName = 'caseStudy';

    const queryOptions = new QueryBuilder().contentType(contentType).limit(1000).include(10).locale(locale);

    const entries: EntryCollection<ContentfulCaseStudyEntry> = await contentfulClientApi.getEntries(queryOptions.build());

    while (entries.total > entries.items.length) {
      // eslint-disable-next-line no-await-in-loop
      const nextEntries: EntryCollection<ContentfulCaseStudyEntry> = await contentfulClientApi.getEntries(queryOptions.set('skip', entries.items.length).build());

      entries.items.push(...nextEntries.items);
    }

    const response = this.normalizeEntryCollection(entries);

    return response;
  });

  private static normalizeEntryCollection({ items }: EntryCollection<ContentfulCaseStudyEntry>): ContentfulCaseStudy[] {
    return items.map((item) => ({
      sys: { id: item.sys.id },
      visibility: item.fields?.visibility ?? '',
      slug: item.fields?.slug ?? '',
      title: item.fields?.title ?? '',
      excerpt: item.fields?.excerpt ?? '',
      link: item.fields?.link ?? '',
      image: { url: item.fields.images?.[0]?.fields?.file?.url ?? '' },
      segments:
        item.fields?.segments?.map((segment) => ({
          sys: { id: segment.sys?.id ?? '' },
          name: segment.fields?.name ?? '',
          industry: {
            sys: { id: segment.fields?.industry?.sys?.id ?? '' },
            name: segment.fields?.industry?.fields?.name ?? '',
            image: { url: segment.fields?.industry?.fields?.image?.fields?.file?.url ?? '' },
            slug: segment.fields?.industry?.fields?.slug ?? ''
          }
        })) ?? [],
      processingMethod: {
        id: item.fields?.processingMethod?.sys?.id ?? '',
        name: item.fields?.processingMethod?.fields?.name ?? ''
      },
      products: item.fields?.products?.filter((p) => p.fields).map(ContentfulProductUtils.normalizeEntry) ?? [],
      features:
        item.fields?.features?.map((feature) => ({
          id: feature.sys?.id ?? '',
          name: feature.fields?.name ?? ''
        })) ?? [],
      images: [],
      isConfidential: item.fields?.confidential ?? false
    }));
  }
}

/* c8 ignore end */
